import { CreateScheduledPostParams, ScheduledPostsClient, ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQueryClient } from 'react-query'
import { scheduledPostsKeys } from './scheduledPostsKeys'
import { useCurrentArticleId } from 'hooks/useCurrentArticleId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'

type UpdateScheduledPostOptions = {
  onSuccess?: (data: any) => void
  suppressToast?: boolean
}

type CacheData = ScheduledPostShow | undefined

export const useUpdateScheduledPost = ({ onSuccess, suppressToast }: UpdateScheduledPostOptions = {}) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const articleId = useCurrentArticleId()
  const { isIOSNative } = useResponsiveDesign()

  return useMutation(
    'updateScheduledPost',
    async ({ id, params }: { id: number; params: Partial<CreateScheduledPostParams> }) => {
      const res = await ScheduledPostsClient.update({ workspaceId, scheduledPostId: id, params })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Error updating scheduled post')
      }
      return res.data.scheduled_post
    },
    {
      onMutate: async ({ id, params }) => {
        const queryKey = scheduledPostsKeys.show(id)
        queryClient.cancelQueries(queryKey)
        const existingPost = queryClient.getQueryData<CacheData>(queryKey)
        const newPost: any = {}
        if (existingPost) {
          if (params.postAt !== undefined) newPost.post_at = params.postAt
          if (params.postData !== undefined) newPost.post_data = params.postData
          if (params.externalIntegrationId !== undefined) newPost.external_integration_id = params.externalIntegrationId
          if (params.mediaObjectIds !== undefined) newPost.media_object_ids = params.mediaObjectIds
          if (params.status === 'scheduled') newPost.status = params.status
          if (params.content !== undefined) newPost.content = params.content
          if (params.alternativeOptions !== undefined) newPost.alternative_options = params.alternativeOptions
          if (params.postSettings !== undefined) newPost.post_settings = params.postSettings
          queryClient.setQueryData<CacheData>(queryKey, { ...existingPost, ...newPost })
        }
        return existingPost
      },
      onSuccess: (res) => {
        if (!suppressToast) {
          showToastMessage('Scheduled post updated successfully', 'success')
        }

        if (isIOSNative) {
          sendMessageToNativeApp('scheduledPostChanged')
        }

        onSuccess?.(res)
      },
      onError: (_, variables, existingPost) => {
        if (existingPost) {
          queryClient.setQueryData<CacheData>(scheduledPostsKeys.show(variables.id), existingPost)
        }
        showToastMessage('Error updating scheduled post', 'error')
      },
      onSettled: (_, __, variables) => {
        queryClient.invalidateQueries(scheduledPostsKeys.show(variables.id))
        if (articleId) {
          queryClient.invalidateQueries(scheduledPostsKeys.article(articleId))
        }
        queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))
        queryClient.invalidateQueries(scheduledPostsKeys.calendar(workspaceId))
        queryClient.invalidateQueries(scheduledPostsKeys.calendar(workspaceId))
        queryClient.invalidateQueries('calendarItems')
      },
    },
  )
}
